import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import '../styles/AddProblemsModal.css';

interface AddProblemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (leetcodeNumbers: number[], generateMissing: boolean) => Promise<void>;
  collectionName: string;
}

export const AddProblemsModal: React.FC<AddProblemsModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  collectionName,
}) => {
  const [leetcodeNumbers, setLeetcodeNumbers] = useState('');
  const [generateMissing, setGenerateMissing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!leetcodeNumbers.trim()) {
      setError('Please enter at least one problem number');
      return;
    }

    // Parse and validate problem numbers (handle both commas and spaces)
    const numbers = leetcodeNumbers
      .split(/[\s,]+/)
      .map(n => n.trim())
      .filter(n => n !== '')
      .map(n => parseInt(n, 10));

    const invalidNumbers = numbers.filter(n => isNaN(n) || n <= 0);
    if (invalidNumbers.length > 0) {
      setError('Please enter valid problem numbers');
      return;
    }

    if (numbers.length === 0) {
      setError('Please enter at least one problem number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onAdd(numbers, generateMissing);
      // Clear input and close modal on success
      setLeetcodeNumbers('');
      setGenerateMissing(true); // Reset to default
      onClose();
    } catch (error) {
      console.error('Failed to add problems:', error);
      setError('Failed to add problems. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setLeetcodeNumbers('');
    setGenerateMissing(true); // Reset to default
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="add-problems-modal">
        <h2 className="modal-title">Add Problems to Collection</h2>
        
        <div className="modal-body">
          <label className="form-label">
            Add LeetCode Problems
          </label>
          <textarea
            className="leetcode-input"
            rows={5}
            placeholder="Enter LeetCode numbers separated by commas or spaces
e.g., 323, 12, 35, 100"
            value={leetcodeNumbers}
            onChange={(e) => {
              setLeetcodeNumbers(e.target.value);
              setError(null);
            }}
            disabled={isLoading}
          />
          
          <div className="generate-missing-option">
            <input
              type="checkbox"
              id="generate-missing"
              checked={generateMissing}
              onChange={(e) => setGenerateMissing(e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="generate-missing">
              Automatically generate missing problems with AI
            </label>
          </div>
          
          <p className="help-text">
            {generateMissing 
              ? "Missing problems will be generated using AI (may take up to 5 minutes for 20 problems)"
              : "Only existing problems will be added to the collection"}
          </p>
          {error && <p className="error-text">{error}</p>}
        </div>

        <div className="modal-actions">
          <button
            className="btn-cancel"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn-add-collection"
            onClick={handleAdd}
            disabled={isLoading || !leetcodeNumbers.trim()}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Adding...
              </>
            ) : (
              <>
                Add to Collection
                <span className="arrow">→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};